import React, { useState, useEffect, useRef, useCallback } from "react";
import "./PdfViewer.scss";
import { useHookstate } from "@hookstate/core";
import globalAuthStateStore from "../../../../../stores/globalAuthStateStore";
import { VirtualLibrarianService } from "../../../../../services/VirtualLibrarianService";
import { Document, Page } from "react-pdf";
import CircularProgress from "@mui/material/CircularProgress";

export default function PdfViewer({ contentId }) {
  const authStore = useHookstate(globalAuthStateStore);
  const vlService = React.useMemo(
    () => new VirtualLibrarianService(authStore),
    [authStore]
  );
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [numPages, setNumPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const loadedPdfUrl = useRef(null);

  const HandleRetrievePdf = useCallback(async () => {
    if (!contentId || loadedPdfUrl.current === contentId) return;

    try {
      const response = await vlService.getSasTokenedContent(contentId);
      if (response?.Success && response.Data?.url) {
        const sasUrl = response.Data.url;
        loadedPdfUrl.current = contentId;
        setSelectedPdf(sasUrl);
      } else {
        console.error(
          "Failed to get SAS tokened URL:",
          response?.DisplayMessage || "No response"
        );
      }
    } catch (error) {
      console.error("Error calling getSasTokenedContent:", error);
    }
  }, [contentId, vlService]);

  useEffect(() => {
    if (contentId) {
      HandleRetrievePdf();
    }

    // Cleanup function to reset loaded PDF when component unmounts or contentId changes
    return () => {
      if (contentId) {
        loadedPdfUrl.current = null;
      }
    };
  }, [contentId, HandleRetrievePdf]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const onDocumentLoadError = (error) => {
    console.error("App.js: PDF Document load error:", error.message);
  };

  return (
    <div className="pdf-viewer-container">
      <div className="pdf-button-container">
        <button
          onClick={() =>
            setPageNumber(pageNumber === 1 ? numPages : pageNumber - 1)
          }
          className="pdf-button"
        >
          Previous
        </button>
        <div>
          {pageNumber}/{numPages}
        </div>
        <button
          onClick={() =>
            setPageNumber(pageNumber === numPages ? 1 : pageNumber + 1)
          }
          className="pdf-button"
        >
          Next
        </button>
      </div>
      <Document
        file={selectedPdf}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        loading={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              padding: "40px 20px",
            }}
          >
            <CircularProgress />
            <p style={{ marginTop: "10px" }}>Loading PDF...</p>
          </div>
        }
        style={{
          width: "100%",
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Page
          pageNumber={pageNumber}
          loading={
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
              <p style={{ marginTop: "10px" }}>Loading page {pageNumber}...</p>
            </div>
          }
          style={{
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            backgroundColor: "white",
            maxWidth: "100%",
            height: "auto",
            overflow: "auto",
            margin: "0 auto",
          }}
          renderTextLayer={true}
        />
      </Document>
    </div>
  );
}
